import { useCallback, useMemo } from 'react';

import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts/types';

import { useItemsAndShortcuts } from '../../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../../controllers/options-provider';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { INTERNAL_GLOBAL_ITEMS } from '../../../ui/more-menu/constants';
import { type SideNavViewedSideNavAppShortcutsSectionAttributesType } from '../../analytics/generated/analytics.types';
import { useAnalyticsEvents } from '../../analytics/generated/use-analytics-events';

export function useNavAnalytics(): {
	fireClickedSideNavItemEvent: (
		id: string,
		navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
		isRightClick?: boolean,
		status?: 'expanded' | 'collapsed' | null,
	) => void;
	fireClickedSideNavSubMenuItemEvent: (
		id: string,
		subItemsCount?: number,
		isInMore?: boolean,
		isRightClick?: boolean,
		navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
		extendedSubItemAnalyticsAttributes?: Record<string, unknown> | null,
	) => void;
	fireDraggedSideNavItemEvent: (id: string, isInMore?: boolean) => void;
	fireViewedShortcutsEvent: (shortcuts: GlobalShortcutsItem[]) => void;
	fireViewedSideNavEvent: () => void;
	fireViewedSideNavSubMenuEvent: (
		id: string,
		subItemsCount?: number,
		isInMore?: boolean,
		navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
	) => void;
} {
	const { fireEvent } = useAnalyticsEvents();
	const { disableMenuCustomization, hasBottomElement } = useOptions();
	const { allItems, globalItems, growthItemShortcuts, items, shortcuts } = useItemsAndShortcuts();

	const {
		hiddenMenuIds,
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		visibleMenuIds,
		visibleItemMenuIds,
		visibleShortcutMenuIds,
	} = useUserMenuDataShapes();

	// Global items + customize/more + bottom element
	const globalItemsList = useMemo(
		() => [
			...visibleItemMenuIds.filter((id) => globalItems.has(id)),
			...(!disableMenuCustomization
				? [
						!!hiddenItemMenuIds.length || !!hiddenShortcutMenuIds.length
							? INTERNAL_GLOBAL_ITEMS.more
							: INTERNAL_GLOBAL_ITEMS.customize,
					]
				: []),
			...(hasBottomElement ? [INTERNAL_GLOBAL_ITEMS.bottomElem] : []),
		],
		[
			disableMenuCustomization,
			hasBottomElement,
			hiddenItemMenuIds,
			hiddenShortcutMenuIds,
			globalItems,
			visibleItemMenuIds,
		],
	);

	// App specific items
	const appSpecificItemsList = useMemo(
		() => visibleItemMenuIds.filter((id) => allItems[id] && !globalItems.has(id)),
		[allItems, globalItems, visibleItemMenuIds],
	);

	// Shortcuts + growth shortcuts
	const shortcutIds = useMemo(() => Object.keys(growthItemShortcuts ?? {}), [growthItemShortcuts]);
	const shortcutItemList = useMemo(
		() => [...visibleShortcutMenuIds, ...shortcutIds],
		[visibleShortcutMenuIds, shortcutIds],
	);

	// All visible items
	const navItemList = useMemo(
		() => [...globalItemsList, ...appSpecificItemsList, ...shortcutItemList],
		[globalItemsList, appSpecificItemsList, shortcutItemList],
	);

	// Index map for O(1) lookup performance
	const visibleMenuIdIndexMap = useMemo(() => {
		const map = new Map<string, number>();
		visibleMenuIds.forEach((id, index) => map.set(id, index));
		return map;
	}, [visibleMenuIds]);

	// Item group
	const itemsIdsSet = useMemo(() => new Set(Object.keys(items)), [items]);
	const getItemGroup = useCallback(
		(id: string) => {
			return !!growthItemShortcuts?.[id] || !!shortcuts?.[id]
				? 'appShortcut'
				: itemsIdsSet.has(id)
					? 'appSpecific'
					: 'global';
		},
		[growthItemShortcuts, itemsIdsSet, shortcuts],
	);

	const getGroupItems = useCallback(
		(group: string) => {
			switch (group) {
				case 'global':
					return globalItemsList;
				case 'appSpecific':
					return appSpecificItemsList;
				case 'appShortcut':
					return shortcutItemList;
				default:
					return [];
			}
		},
		[appSpecificItemsList, globalItemsList, shortcutItemList],
	);

	const fireViewedSideNavEvent = useCallback((): void => {
		const moreMenuItems = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

		fireEvent('ui.sideNav.viewed.sideNavMenu', {
			appSpecificItemsCount: appSpecificItemsList.length,
			appSpecificItemsList,
			appShortcutItemsCount: shortcutItemList.length,
			appShortcutItemsList: shortcutItemList,
			globalItemsCount: globalItemsList.length,
			globalItemsList,
			inMoreItemsCount: moreMenuItems.length,
			inMoreItemsList: moreMenuItems,
		});
	}, [
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		globalItemsList,
		appSpecificItemsList,
		shortcutItemList,
		fireEvent,
	]);

	const fireClickedSideNavItemEvent = useCallback(
		(
			id: string,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
			isRightClick: boolean = false,
			status: 'expanded' | 'collapsed' | null = null,
		): void => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);
			const isInternalGlobalItem = Object.values(INTERNAL_GLOBAL_ITEMS).includes(id);
			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const isInMore =
				// customize is in more if there are any hidden items
				(INTERNAL_GLOBAL_ITEMS.customize === id && !!hiddenMenuIds.length) ||
				hiddenMenuIds.includes(id);

			// Current index irrespective of the item group
			const currentIndex = isInMore
				? -1
				: // More and customise and growth shortcuts are always at the end of the list
					isInternalGlobalItem || !!growthItemShortcuts?.[id]
					? visibleMenuIdIndexMap.size
					: (visibleMenuIdIndexMap.get(id) ?? -1);

			// Current index within the item group
			const currentGroupIndex = isInMore ? -1 : visibleGroupItems.indexOf(id);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.clicked.sideNavItem', {
				navLayoutComponent: currentComponent,
				navItemsCount: navItemList.length,
				navItemsIndex: currentIndex,
				group: currentGroup,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: currentGroupIndex,
				groupItem: id,
				isInMore,
				isRightClick,
				status,
			});
		},
		[
			fireEvent,
			getGroupItems,
			getItemGroup,
			growthItemShortcuts,
			hiddenMenuIds,
			navItemList.length,
			visibleMenuIdIndexMap,
		],
	);

	const fireDraggedSideNavItemEvent = useCallback(
		(id: string, isInMore: boolean = false): void => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);
			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			// Current index irrespective of the item group
			const currentIndex = isInMore
				? -1
				: // growth shortcuts are always at the end of the list
					!!growthItemShortcuts?.[id]
					? visibleMenuIdIndexMap.size
					: (visibleMenuIdIndexMap.get(id) ?? -1);

			// Current index within the item group
			const currentGroupIndex = isInMore ? -1 : visibleGroupItems.indexOf(id);

			const currentComponent = isInMore ? 'postMain' : 'main';

			fireEvent('ui.sideNav.dragged.sideNavItem', {
				navLayoutComponent: currentComponent,
				navItemsCount: navItemList.length,
				navItemsIndex: currentIndex,
				group: currentGroup,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: currentGroupIndex,
				groupItem: id,
				isInMore,
			});
		},
		[
			fireEvent,
			getGroupItems,
			getItemGroup,
			growthItemShortcuts,
			navItemList.length,
			visibleMenuIdIndexMap,
		],
	);

	const fireViewedSideNavSubMenuEvent = useCallback(
		(
			id: string,
			subItemsCount?: number,
			isInMore: boolean = false,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
		): void => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);

			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.viewed.sideNavSubMenu', {
				group: currentGroup,
				groupItem: id,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: visibleGroupItems.indexOf(id),
				groupSubItemCount: subItemsCount ?? null,
				isInMore,
				navLayoutComponent: currentComponent,
			});
		},
		[fireEvent, getGroupItems, getItemGroup],
	);

	const fireViewedShortcutsEvent = useCallback(
		(shortcuts: GlobalShortcutsItem[]): void => {
			const attributes: SideNavViewedSideNavAppShortcutsSectionAttributesType = {
				appShortcutItemsCount: shortcuts.length,
				appShortcutItemsList: shortcuts.map((item) => item.label),
			};
			fireEvent('ui.sideNav.viewed.sideNavAppShortcutsSection', attributes);
		},
		[fireEvent],
	);

	const fireClickedSideNavSubMenuItemEvent = useCallback(
		(
			id: string,
			subItemsCount?: number,
			isInMore: boolean = false,
			isRightClick: boolean = false,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
			extendedSubItemAnalyticsAttributes?: Record<string, unknown> | null,
		): void => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);

			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.clicked.sideNavSubItem', {
				group: currentGroup,
				groupItem: id,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: visibleGroupItems.indexOf(id),
				groupSubItem: null,
				groupSubItemCount: subItemsCount ?? null,
				groupSubItemIndex: null,
				isInMore,
				isRightClick,
				navLayoutComponent: currentComponent,
				extendedAnalyticsAttributes: extendedSubItemAnalyticsAttributes ?? null,
			});
		},
		[fireEvent, getGroupItems, getItemGroup],
	);

	return {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireDraggedSideNavItemEvent,
		fireViewedShortcutsEvent,
		fireViewedSideNavEvent,
		fireViewedSideNavSubMenuEvent,
	};
}
